import type { SxProps, Theme } from '@mui/material/styles';

type HeaderSection = 'asFound' | 'asLeft' | 'neutral';

export const getHeaderSectionColors = (
  section: HeaderSection,
  isDarkMode: boolean
) => {
  const colors = {
    asFound: {
      main: isDarkMode
        ? 'rgba(33, 150, 243, 0.15)'
        : 'rgba(33, 150, 243, 0.04)',
      light: isDarkMode
        ? 'rgba(33, 150, 243, 0.08)'
        : 'rgba(33, 150, 243, 0.02)',
      border: isDarkMode ? 'rgba(33, 150, 243, 0.4)' : 'rgba(33, 150, 243, 0.2)'
    },
    asLeft: {
      main: isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.04)',
      light: isDarkMode ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.02)',
      border: isDarkMode ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.2)'
    },
    neutral: {
      main: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
      light: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
      border: 'divider'
    }
  };

  return colors[section];
};

export const getStickyHeaderSx = (
  section: HeaderSection,
  isDarkMode: boolean,
  top: string | number = 0,
  zIndex: number = 100
): SxProps<Theme> => {
  const colors = getHeaderSectionColors(section, isDarkMode);

  return {
    position: 'sticky',
    top,
    zIndex,
    backgroundColor: colors.main,
    borderBottom:
      section === 'neutral' && isDarkMode
        ? '1px solid rgba(255, 255, 255, 0.05)'
        : undefined
  };
};

export const getSectionHeaderSx = (
  section: 'asFound' | 'asLeft',
  isDarkMode: boolean,
  top: string | number = 0,
  zIndex: number = 100
): SxProps<Theme> => {
  const colors = getHeaderSectionColors(section, isDarkMode);

  return {
    backgroundColor: colors.main,
    borderLeft: `2px solid ${colors.border}`,
    borderRight: `2px solid ${colors.border}`,
    fontWeight: 600,
    fontSize: '1rem',
    position: 'sticky',
    top,
    zIndex,
    padding: '4px 8px'
  };
};

export const getSubHeaderSx = (
  section: 'asFound' | 'asLeft',
  isDarkMode: boolean,
  top: string | number = '40px',
  zIndex: number = 99,
  withBorder: boolean = false
): SxProps<Theme> => {
  const colors = getHeaderSectionColors(section, isDarkMode);

  return {
    backgroundColor: colors.light,
    position: 'sticky',
    top,
    zIndex,
    borderBottom: isDarkMode
      ? '1px solid rgba(255, 255, 255, 0.05)'
      : undefined,
    ...(withBorder && {
      borderRight: `2px solid ${colors.border}`
    })
  };
};

export const getStickyBodyCellSx = (
  _section: HeaderSection,
  left?: number
): SxProps<Theme> => {
  return {
    position: 'sticky',
    left: left ?? 0,
    backgroundColor: 'background.paper',
    zIndex: 1
  };
};

export const getBodyCellSx = (
  section: HeaderSection,
  isDarkMode: boolean
): SxProps<Theme> => {
  const colors = getHeaderSectionColors(section, isDarkMode);

  if (section === 'neutral') {
    return {};
  }

  return {
    backgroundColor: colors.light
  };
};
